import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    createTheme,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    Stack,
    Tooltip,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { copyObject, highlightStyle } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { LegalHistoryModel } from '../../../Models/LegalHistory/LegalHistory.model';
import { fetchLegalHistoryInfo, fetchLegalHistoryInfoByMemberId, saveLegalHistoryInfo, setLegalHistoryInfo } from '../../../store/legalHistorySlice.slice';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { NO, YES } from '../../../utils/constants';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Legal_History_Mini_Mental_Exam.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '90%',
        },
    },
};

const yesNoList = [{ Answer: YES }, { Answer: NO }];

const LegalHistory: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const { legalHistoryInfo } = useSelector((state: RootState) => state.legalHistorySlice);
    const isSaving = isSavingEngagementDraft;
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const legalHistoryInfoIsComplete = (values: LegalHistoryModel) => {
        const isComplete =
            Boolean(values.HasCOT) &&
            (Boolean(values.LegalJusticeInvolvement === NO) ||
                (Boolean(values.LegalJusticeInvolvement === YES) &&
                    (Boolean(values.YesAsJuvenile) ||
                        Boolean(values.YesPastNoIncarceration) ||
                        Boolean(values.YesOnProbataionNoIncarceration) ||
                        Boolean(values.YesPreviouslyIncarceratedNotOnParole) ||
                        Boolean(values.YesPreviouslyIncarceratedOnParole) ||
                        Boolean(values.YesCurrentlyChargedWarrents) ||
                        Boolean(values.No))));
        return isComplete;
    };

    const setFieldAndClearAIFlag = (fieldName: string, value: string | boolean) => {
        const next: any = { ...legalHistoryInfo, [fieldName]: value, IsAILegalJusticeInvolvement: false, IsAIHasCOT: false };

        dispatch(setLegalHistoryInfo(next));
        setIsChanged(true);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldAndClearAIFlag(event.target.name, event.target.checked);
    };

    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (legalHistoryInfo: LegalHistoryModel, isComplete = false) => {
        const copy = copyObject(legalHistoryInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveLegalHistoryInfo(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveLegalHistoryInfo(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setLegalHistoryInfo(new LegalHistoryModel()));
        dispatch(gotoNextMenu());
    };

    useEffect(() => {
        if (engagementDraft?.LegalHistoryId) {
            dispatch(fetchLegalHistoryInfo(engagementDraft.LegalHistoryId));
        } else {
            dispatch(fetchLegalHistoryInfoByMemberId(memberId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
            <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                <Grid>
                    {isSaving ? (
                        <Box display="flex" justifyContent="center" className="loader-container">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2} sx={classes.root}>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <Typography variant="h6">Legal History/Justice Involvement</Typography>
                                        <Tooltip title="Legal History Help Document">
                                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                                        </Tooltip>
                                    </Stack>

                                    {isBHDiagnostic && (
                                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                            * Required for BH Diagnostic
                                        </Typography>
                                    )}
                                    <hr />
                                </Grid>
                                <Grid size={6}>
                                    <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                        {AssessmentQuestions.LegalHistory_HasLegalIssues}
                                        {legalHistoryInfo.IsAILegalJusticeInvolvement && isAIIcon()}
                                    </FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    <RadioGroup
                                        row
                                        name="LegalJusticeInvolvement"
                                        value={legalHistoryInfo.LegalJusticeInvolvement || ''}
                                        onChange={(e) => {
                                            setFieldAndClearAIFlag('LegalJusticeInvolvement', e.target.value);
                                        }}
                                    >
                                        {yesNoList.map((type, index) => {
                                            return (
                                                <FormControlLabel key={`answer-label-${index}`} value={type.Answer} control={<Radio />} label={type.Answer} />
                                            );
                                        })}
                                    </RadioGroup>
                                </Grid>

                                <Collapse in={legalHistoryInfo.LegalJusticeInvolvement === YES} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                                    <Grid size={6} sx={{ ml: 'auto', md: { ml: '' } }}>
                                        <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={<Checkbox checked={legalHistoryInfo.YesAsJuvenile} onChange={handleChange} name="YesAsJuvenile" />}
                                                    label={AssessmentQuestions.LegalHistory_AsJuvenile}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={legalHistoryInfo.YesPastNoIncarceration}
                                                            onChange={handleChange}
                                                            name="YesPastNoIncarceration"
                                                        />
                                                    }
                                                    label={AssessmentQuestions.LegalHistory_InPastNoIncarceration}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={legalHistoryInfo.YesOnProbataionNoIncarceration}
                                                            onChange={handleChange}
                                                            name="YesOnProbataionNoIncarceration"
                                                        />
                                                    }
                                                    label={AssessmentQuestions.LegalHistory_CurrentlyProbationNoIncarceration}
                                                />
                                                {legalHistoryInfo.YesOnProbataionNoIncarceration && (
                                                    <TextField
                                                        label="Name & phone of probation/parole officer"
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        margin="normal"
                                                        name="YesOnProbataionNoIncarcerationDetails"
                                                        value={legalHistoryInfo.YesOnProbataionNoIncarcerationDetails || ''}
                                                        onChange={(e) => {
                                                            setFieldAndClearAIFlag('YesOnProbataionNoIncarcerationDetails', e.target.value);
                                                        }}
                                                    />
                                                )}
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={legalHistoryInfo.YesPreviouslyIncarceratedNotOnParole}
                                                            onChange={handleChange}
                                                            name="YesPreviouslyIncarceratedNotOnParole"
                                                        />
                                                    }
                                                    label={AssessmentQuestions.LegalHistory_PrevIncarceratedNoProbation}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={legalHistoryInfo.YesPreviouslyIncarceratedOnParole}
                                                            onChange={handleChange}
                                                            name="YesPreviouslyIncarceratedOnParole"
                                                        />
                                                    }
                                                    label={AssessmentQuestions.LegalHistory_PrevIncarceratedYesprobation}
                                                />
                                                {legalHistoryInfo.YesPreviouslyIncarceratedOnParole && (
                                                    <TextField
                                                        label="Name & phone of probation/parole officer"
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        margin="normal"
                                                        name="YesPreviouslyIncarceratedOnParoleDetails"
                                                        value={legalHistoryInfo.YesPreviouslyIncarceratedOnParoleDetails || ''}
                                                        onChange={(e) => {
                                                            setFieldAndClearAIFlag('YesPreviouslyIncarceratedOnParoleDetails', e.target.value);
                                                        }}
                                                    />
                                                )}
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={legalHistoryInfo.YesCurrentlyChargedWarrents}
                                                            onChange={handleChange}
                                                            name="YesCurrentlyChargedWarrents"
                                                        />
                                                    }
                                                    label={AssessmentQuestions.LegalHistory_CurrentlyCharged}
                                                />
                                                <FormControlLabel
                                                    control={<Checkbox checked={legalHistoryInfo.No} onChange={handleChange} name="No" />}
                                                    label="No"
                                                />
                                            </FormGroup>
                                        </FormControl>
                                        <TextField
                                            label={AssessmentQuestions.LegalHistory_Comments}
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            rows={3}
                                            fullWidth
                                            margin="normal"
                                            name="LegalHistoryComments"
                                            value={legalHistoryInfo.LegalHistoryComments || ''}
                                            onChange={(e) => {
                                                setFieldAndClearAIFlag('LegalHistoryComments', e.target.value);
                                            }}
                                        />
                                    </Grid>
                                </Collapse>

                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <hr className="hr-lite" />
                                </Grid>

                                <Grid size={6}>
                                    <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                        Member has Court-Ordered Treatment (COT)
                                        {legalHistoryInfo.IsAIHasCOT && isAIIcon()}
                                    </FormLabel>
                                </Grid>
                                <Grid size={6}>
                                    <RadioGroup
                                        row
                                        name="HasCOT"
                                        value={legalHistoryInfo.HasCOT || ''}
                                        onChange={(e) => {
                                            setFieldAndClearAIFlag('HasCOT', e.target.value);
                                        }}
                                    >
                                        {yesNoList.map((type, index) => {
                                            return (
                                                <FormControlLabel key={`answer-label-${index}`} value={type.Answer} control={<Radio />} label={type.Answer} />
                                            );
                                        })}
                                    </RadioGroup>
                                </Grid>
                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                    <hr className="hr-lite" />
                                </Grid>
                            </Grid>

                            <Grid justifyContent="flex-end" className="content-footer">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ marginRight: 2 }}
                                    disabled={isSaving}
                                    onClick={() => dispatch(gotoPreviousMenu())}
                                    startIcon={<ArrowBackIosIcon />}
                                >
                                    Previous
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSaving}
                                    endIcon={isSaving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                    onClick={handleSubmit}
                                >
                                    Next
                                </Button>
                            </Grid>
                        </>
                    )}

                    {Boolean(legalHistoryInfoIsComplete(legalHistoryInfo) && legalHistoryInfo.CreatedBy && !isChanged) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title={`Legal History/Justice Involvement`}
                            buttonsDirection="column-reverse"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText={`Legal History/Justice Involvement Assessment Not Reviewed`}
                            okText={`Agree With Legal History/Justice Involvement Assessment`}
                            onConfirm={() => handleOnConfirmToNext(legalHistoryInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message={`Last Legal History/Justice Involvement assessment completed on ${(() => {
                                const d = toDate(legalHistoryInfo.UpdatedDate ?? legalHistoryInfo.CreatedDate);
                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                            })()}.`}
                        />
                    ) : Boolean(legalHistoryInfoIsComplete(legalHistoryInfo)) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Legal History/Justice Involvement"
                            cancelText="Go Back"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(legalHistoryInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            showBottomCenter={true}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message="I confirm and acknowledge that Legal History/Justice Involvement is completed."
                        />
                    ) : (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Legal History/Justice Involvement"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText="Skip and continue"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(legalHistoryInfo, false)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            renderBody={() =>
                                BHDiagnosticConfirmationBody({
                                    assessmentName: 'Legal History/Justice Involvement',
                                    diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                                })
                            }
                        />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};
export default LegalHistory;
